// ---------------------------------------------------------------------------
// Request-params builder for the Access Legitimacy events grid (design v6 §3.3).
//
// Pure mapping from container state → the `POST /api/v1/dashboard/access-legitimacy`
// request body. Converts page → offset, carries the date-only `sortDir` + a
// fixed page size, and OMITS empty optionals so the backend's "absent means
// unfiltered" defaults apply (no `sensitivity` when "All"; no `userTypes` /
// `fileTypes` when empty; no `search` when blank). Array params are sorted so
// the query key is stable across selection order.
// ---------------------------------------------------------------------------

import {
  ACCESS_LEGITIMACY_PAGE_SIZE,
  type AccessLegitimacySortDir,
  type ConnectedService,
  type TimeFrameParam,
} from '@dawn/feature-config';

import type {
  AccessDiscoveryFileLink,
  AccessDiscoveryPrincipalLink,
  AccessLegitimacyScope,
} from './access-legitimacy-scope';

/** Container-owned filter / sort / page state. */
export interface AccessLegitimacyFilterState {
  /** 0-indexed page. */
  page: number;
  sortDir: AccessLegitimacySortDir;
  /** `'all'` or a catalogued sensitivity label. */
  selectedSensitivity: string;
  selectedUserTypes: readonly string[];
  selectedFileTypes: readonly string[];
  /** The already-debounced search term. */
  debouncedSearch: string;
  /** Access Discovery node scope carried in the URL (TDEV-1046); absent means unscoped. */
  scope?: AccessLegitimacyScope;
  /** Drill-down time range from the URL (TDEV-1114); absent means the API's 180 days. */
  timeFrame?: TimeFrameParam;
  /** Drill-down service from the URL (TDEV-1114); absent means every service. */
  service?: ConnectedService;
  /** One file from an Overview row, from the URL (TDEV-1116). */
  file?: AccessDiscoveryFileLink;
  /** One identity from an Overview row, from the URL (TDEV-1116). */
  principal?: AccessDiscoveryPrincipalLink;
  /** The chart cutoff a drill-down came from; absent means the now-anchored read log. */
  asOf?: string;
  /** The card the drill came from counts analysed files only. */
  analysedOnly?: boolean;
}

/** The request body sent to the events-grid endpoint (mirrors `AccessLegitimacyQueryDto`). */
export interface AccessLegitimacyRequestParams {
  offset: number;
  limit: number;
  sortDir: AccessLegitimacySortDir;
  sensitivity?: string;
  userTypes?: string[];
  fileTypes?: string[];
  search?: string;
  identityBucket?: string;
  topic?: string;
  documentTypes?: string[];
  timeFrame?: number;
  service?: string;
  fileKey?: string;
  fileSource?: string;
  principalKey?: string;
  /** Sent as a header, never in the body: the contract the scope's link was written under. */
  scopeContract?: number;
  /** Sent as a header, never in the body: the chart cutoff the grid is anchored to. */
  asOf?: string;
  /** Sent as a header, never in the body: leave out files never analysed. */
  analysedOnly?: boolean;
  clusterPath?: string[];
  providerVersion?: string;
  clusterTreeVersion?: string;
}

export function buildAccessLegitimacyParams(
  state: AccessLegitimacyFilterState
): AccessLegitimacyRequestParams {
  const search = state.debouncedSearch.trim();
  return {
    offset: state.page * ACCESS_LEGITIMACY_PAGE_SIZE,
    limit: ACCESS_LEGITIMACY_PAGE_SIZE,
    sortDir: state.sortDir,
    ...(state.selectedSensitivity && state.selectedSensitivity !== 'all'
      ? { sensitivity: state.selectedSensitivity }
      : {}),
    ...(state.selectedUserTypes.length > 0
      ? { userTypes: [...state.selectedUserTypes].sort() }
      : {}),
    ...(state.selectedFileTypes.length > 0
      ? { fileTypes: [...state.selectedFileTypes].sort() }
      : {}),
    ...(search.length > 0 ? { search } : {}),
    ...(state.scope?.identity ? { identityBucket: state.scope.identity.id } : {}),
    ...(state.scope?.data ? { topic: state.scope.data.topic } : {}),
    ...(state.scope?.data && state.scope.data.documentTypes.length > 0
      ? { documentTypes: [...state.scope.data.documentTypes].sort() }
      : {}),
    ...(state.timeFrame ? { timeFrame: Number(state.timeFrame) } : {}),
    ...(state.asOf ? { asOf: state.asOf } : {}),
    ...(state.analysedOnly ? { analysedOnly: true } : {}),
    ...(state.service ? { service: state.service } : {}),
    ...(state.file ? { fileKey: state.file.key, fileSource: state.file.source } : {}),
    ...(state.principal ? { principalKey: state.principal.key } : {}),
    ...(state.scope?.scopeContract !== undefined
      ? { scopeContract: state.scope.scopeContract }
      : {}),
    // Sent as parsed, even incomplete: the API rejects a partial cluster rather
    // than this dropping it and widening the grid to the department.
    ...(state.scope?.data?.cluster
      ? {
          clusterPath: [...state.scope.data.cluster.path],
          providerVersion: state.scope.data.cluster.providerVersion,
          clusterTreeVersion: state.scope.data.cluster.treeVersion,
        }
      : {}),
  };
}
